/**
 * AvailabilitySettings — admin-only component
 * Lets the admin set 24/7 or specific working hours per day.
 * Backed by the `availability` table in Lovable Cloud.
 */
import { useState, useEffect } from 'react';
import {
  Clock, CheckCircle2, AlertCircle, Save, Globe,
  Sun, Zap,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cachedQuery, invalidatePrefetch, PREFETCH_KEYS } from '@/lib/admin-prefetch';
import { useSession } from '@/lib/auth/auth-client';
import CalendarConnections from '@/components/availability/CalendarConnections';

const GRAD    = '#5048E5';
const ORANGE  = '#f97316';
const fg      = 'hsl(var(--foreground))';
const fgMuted = 'hsl(var(--muted-foreground))';
const border  = 'hsl(var(--border))';
const surface = 'hsl(var(--card))';
const muted   = 'hsl(var(--muted))';

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
] as const;

type DayKey = typeof DAYS[number]['key'];

type DaySlot = { enabled: boolean; start: string; end: string };
type Schedule = Record<DayKey, DaySlot>;

const DEFAULT_SCHEDULE: Schedule = {
  mon: { enabled: true,  start: '09:00', end: '17:00' },
  tue: { enabled: true,  start: '09:00', end: '17:00' },
  wed: { enabled: true,  start: '09:00', end: '17:00' },
  thu: { enabled: true,  start: '09:00', end: '17:00' },
  fri: { enabled: true,  start: '09:00', end: '17:00' },
  sat: { enabled: false, start: '10:00', end: '14:00' },
  sun: { enabled: false, start: '10:00', end: '14:00' },
};

const TIMEZONES = [
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
];

const TIME_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (const m of ['00', '30']) {
    TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:${m}`);
  }
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors"
      style={{ background: checked ? GRAD : 'hsl(var(--muted))', border: `1px solid ${checked ? GRAD : border}` }}>
      <span
        className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
        style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }}
      />
    </button>
  );
}

function TimeSelect({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className="min-w-0 flex-1 rounded-lg px-2 py-1.5 text-xs outline-none transition-all sm:flex-none"
      style={{
        background: muted,
        border: `1px solid ${border}`,
        color: disabled ? fgMuted : fg,
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}>
      {TIME_OPTIONS.map(t => (
        <option key={t} value={t}>{t}</option>
      ))}
    </select>
  );
}

export default function AvailabilitySettings() {
  const { user } = useSession();
  const [mode,       setMode]       = useState<'always' | 'scheduled'>('always');
  const [schedule,   setSchedule]   = useState<Schedule>(DEFAULT_SCHEDULE);
  const [statusNote, setStatusNote] = useState('');
  const [timezone,   setTimezone]   = useState('Europe/London');
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [success,    setSuccess]    = useState('');
  const [error,      setError]      = useState('');

  useEffect(() => {
    if (!user) return;
    let active = true;
    const key = PREFETCH_KEYS.availability(user.id);
    cachedQuery(key, () =>
      Promise.resolve(supabase
        .from('availability')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()))
      .then(({ data }) => {
        if (!active || !data) { if (active) setLoading(false); return; }
        setMode((data.mode as 'always' | 'scheduled') ?? 'always');
        const sched = data.schedule as Partial<Schedule> | null;
        if (sched && Object.keys(sched).length) setSchedule({ ...DEFAULT_SCHEDULE, ...sched });
        setStatusNote(data.status_note ?? '');
        setTimezone(data.timezone ?? 'Europe/London');
        setLoading(false);
      });
    return () => { active = false; invalidatePrefetch(key); };
  }, [user]);

  function updateDay(day: DayKey, field: keyof DaySlot, value: boolean | string) {
    setSchedule(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  }

  async function handleSave() {
    if (!user) return;
    setError(''); setSuccess('');
    setSaving(true);
    try {
      const { error: err } = await supabase
        .from('availability')
        .upsert(
          { user_id: user.id, mode, schedule, status_note: statusNote, timezone },
          { onConflict: 'user_id' },
        );
      if (err) throw err;
      setSuccess('Availability saved successfully.');
      setTimeout(() => setSuccess(''), 4000);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="h-6 w-6 animate-spin rounded-full border-b-2" style={{ borderColor: GRAD }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <CalendarConnections />

      <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
        <div className="mb-4 flex items-center gap-2">
          <Clock size={16} style={{ color: GRAD }} />
          <h3 className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
            Availability Mode
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setMode('always')}
            className="flex items-start gap-3 rounded-xl p-4 text-left transition-all"
            style={{
              background: mode === 'always' ? 'hsla(243,75%,59%,0.08)' : muted,
              border: `2px solid ${mode === 'always' ? GRAD : border}`,
            }}>
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl"
              style={{ background: mode === 'always' ? GRAD : 'hsl(var(--muted-foreground)/0.15)' }}>
              <Zap size={15} style={{ color: mode === 'always' ? '#fff' : fgMuted }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
                Available 24/7
              </p>
              <p className="mt-0.5 text-xs leading-relaxed" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Always shown as available to clients
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setMode('scheduled')}
            className="flex items-start gap-3 rounded-xl p-4 text-left transition-all"
            style={{
              background: mode === 'scheduled' ? 'hsla(243,75%,59%,0.08)' : muted,
              border: `2px solid ${mode === 'scheduled' ? GRAD : border}`,
            }}>
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl"
              style={{ background: mode === 'scheduled' ? GRAD : 'hsl(var(--muted-foreground)/0.15)' }}>
              <Sun size={15} style={{ color: mode === 'scheduled' ? '#fff' : fgMuted }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
                Specific Hours
              </p>
              <p className="mt-0.5 text-xs leading-relaxed" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Set working days and hours per day
              </p>
            </div>
          </button>
        </div>
      </div>

      {mode === 'scheduled' && (
        <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sun size={15} style={{ color: ORANGE }} />
              <h3 className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
                Working Hours
              </h3>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const updated = { ...schedule };
                  (['mon','tue','wed','thu','fri'] as DayKey[]).forEach(d => {
                    updated[d] = { enabled: true, start: '09:00', end: '17:00' };
                  });
                  (['sat','sun'] as DayKey[]).forEach(d => {
                    updated[d] = { ...updated[d], enabled: false };
                  });
                  setSchedule(updated);
                }}
                className="rounded-lg px-2.5 py-1 text-xs font-semibold transition-opacity hover:opacity-80"
                style={{ background: 'hsla(243,75%,59%,0.1)', color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Mon–Fri
              </button>
              <button
                type="button"
                onClick={() => {
                  const updated = { ...schedule };
                  (Object.keys(updated) as DayKey[]).forEach(d => {
                    updated[d] = { enabled: true, start: '09:00', end: '17:00' };
                  });
                  setSchedule(updated);
                }}
                className="rounded-lg px-2.5 py-1 text-xs font-semibold transition-opacity hover:opacity-80"
                style={{ background: 'hsla(243,75%,59%,0.1)', color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Every day
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {DAYS.map(({ key, label }) => {
              const slot = schedule[key];
              return (
                <div
                  key={key}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl px-4 py-3 transition-all"
                  style={{
                    background: slot.enabled ? 'hsla(243,75%,59%,0.05)' : muted,
                    border: `1px solid ${slot.enabled ? 'hsla(243,75%,59%,0.2)' : border}`,
                    opacity: slot.enabled ? 1 : 0.7,
                  }}>
                  <Toggle
                    checked={slot.enabled}
                    onChange={v => updateDay(key, 'enabled', v)}
                    label={`Enable ${label}`}
                  />

                  <span className="min-w-0 flex-1 text-sm font-semibold"
                    style={{ color: slot.enabled ? fg : fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {label}
                  </span>

                  {slot.enabled ? (
                    <div className="flex w-full items-center gap-2 sm:w-auto">
                      <TimeSelect value={slot.start} onChange={v => updateDay(key, 'start', v)} />
                      <span className="text-xs shrink-0" style={{ color: fgMuted }}>to</span>
                      <TimeSelect value={slot.end} onChange={v => updateDay(key, 'end', v)} />
                    </div>
                  ) : (
                    <span className="text-xs shrink-0" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      Unavailable
                    </span>
                  )}
                </div>

              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
        <div className="mb-4 flex items-center gap-2">
          <Globe size={15} style={{ color: GRAD }} />
          <h3 className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
            Timezone & Status
          </h3>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Your timezone
            </label>
            <select
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              className="w-full rounded-xl px-4 py-3 text-sm outline-none"
              style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Status note <span className="normal-case font-normal">(optional)</span>
              </label>
              <span className="text-xs" style={{ color: statusNote.length > 220 ? '#ef4444' : fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {statusNote.length}/255
              </span>
            </div>
            <input
              type="text"
              value={statusNote}
              onChange={e => setStatusNote(e.target.value.slice(0, 255))}
              placeholder='e.g. "Back Monday 9am" or "On holiday until 20th"'
              className="w-full rounded-xl px-4 py-3 text-sm outline-none"
              style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            />
            <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Shown to clients alongside your availability hours.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm"
          style={{ background: 'hsla(0,80%,60%,0.1)', border: '1px solid hsla(0,80%,60%,0.3)', color: 'hsl(0 70% 55%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          <AlertCircle size={15} className="shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm"
          style={{ background: 'hsla(143,70%,45%,0.1)', border: '1px solid hsla(143,70%,45%,0.3)', color: 'hsl(143 60% 40%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          <CheckCircle2 size={15} className="shrink-0" />
          {success}
        </div>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-60"
        style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        <Save size={15} />
        {saving ? 'Saving…' : 'Save Availability'}
      </button>
    </div>
  );
}
